'use client';

import { Button, Divider, Image, TextArea, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { pathsMusinsa } from '../../common';
import { BottomButtonWrapper } from '../../common/components/BottomButtonWrapper';
import { useCuratorSnackbar } from '../../common/hooks/useCuratorSnackbar';
import { curatorQueries } from '../../query-factory';
import { useReverseProposalGoodsDetail } from '../hooks/useReverseProposalGoods';
import { useCreateReverseProposal } from '../hooks/useReverseProposalMutation';
import {
  isMusinsaService,
  trackCampaignSuggestionActionClickMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';

interface SuggestionPageViewProps {
  goodsNo: number;
}

const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;
const MAX_LENGTH = 500;

export const SuggestionPageView = ({ goodsNo }: SuggestionPageViewProps) => {
  const router = useRouter();
  const dialog = useDialog();
  const snackbar = useCuratorSnackbar();
  const { service } = useDeviceInfo();
  const { data: goods, isLoading } = useReverseProposalGoodsDetail(goodsNo);
  const { mutate: createProposal, isPending } = useCreateReverseProposal();
  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfo?.snsTitle ?? '';
  const { data: profile } = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(snsTitle),
    enabled: !!snsTitle,
  });
  const nickname = profile?.nickname ?? '';

  const [dateValue, setDateValue] = useState('');
  const [messageValue, setMessageValue] = useState('');
  const [isMessageError, setIsMessageError] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // NOTE: dayjs는 로컬 타임 기준이라 toISOString()의 UTC 변환 부작용 없음
  const minDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const maxDate = dayjs().add(30, 'day').format('YYYY-MM-DD');

  // NOTE: iOS Safari native date picker는 min/max를 무시하므로 onChange에서 한 번 더 검증
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (!next) {
      setDateValue('');
      return;
    }
    if (next < minDate || next > maxDate) return;
    setDateValue(next);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const filtered = e.target.value.replace(EMOJI_REGEX, '');
    if (filtered.length > MAX_LENGTH) {
      setMessageValue(filtered.slice(0, MAX_LENGTH));
      setIsMessageError(true);
      return;
    }
    setMessageValue(filtered);
    setIsMessageError(false);
  };

  const handleSubmit = () => {
    if (!dateValue || isClicked) return;
    setIsClicked(true);
    if (goods && isMusinsaService(service)) {
      trackCampaignSuggestionActionClickMusinsa({
        name: 'suggestion',
        brandId: goods.brandId,
        id: nickname,
      });
    }

    const startAt = dateValue.replace(/-/g, '');
    createProposal(
      { goodsNo, startAt, message: messageValue },
      {
        onSuccess: () => {
          snackbar.show({ message: '할인 쿠폰 캠페인을 제안했습니다.' });
          router.push(
            pathsMusinsa.campaignCollaboration({
              query: { type: 'MY_PROPOSAL', status: 'WAITING' },
            }),
          );
        },
        onError: () => {
          setIsClicked(false);
        },
      },
    );
  };

  const openCancelDialog = () => {
    if (goods && isMusinsaService(service)) {
      trackCampaignSuggestionActionClickMusinsa({
        name: 'cancel',
        brandId: goods.brandId,
        id: nickname,
      });
    }
    dialog.open({
      title: '할인 쿠폰 캠페인 제안을 취소하시겠습니까?',
      message: '취소하면 입력하신 내용이 모두 삭제됩니다.',
      cancelButtonText: '닫기',
      confirmButtonText: '취소하기',
      onCancel: () => dialog.close(),
      onConfirm: () => {
        dialog.close();
        snackbar.show({ message: '할인 쿠폰 캠페인 제안을 취소했습니다.' });
        router.push(
          pathsMusinsa.campaignCollaboration({ query: { type: 'MY_PROPOSAL', status: 'WAITING' } }),
        );
      },
    });
  };

  if (isLoading || !goods) {
    return null;
  }

  const formatDisplayDate = (value: string) => {
    const [y, m, d] = value.split('-');
    return `${y}.${m}.${d}`;
  };

  return (
    <div className="flex-1 h-full bg-white">
      <div className="w-full px-4">
        {/* 상품 정보 — 레거시 ProductItem paddingOptions top='6' bottom='3' */}
        <a
          href={`${process.env.NEXT_PUBLIC_MUSINSA_BASE_URL}/products/${goodsNo}`}
          className="flex items-center pt-6 pb-3"
        >
          <div className="w-[72px] h-[86px] rounded overflow-hidden relative shrink-0">
            <Image src={goods.imageUrl} alt={goods.goodsName} size="size72x86" />
          </div>
          <div className="flex flex-col w-full ml-3 overflow-hidden [&>span]:truncate">
            <Typography variant="body13pxSemi">{goods.brandName}</Typography>
            <Typography variant="body13pxReg" className="mt-[2px]!">
              {goods.goodsName}
            </Typography>
            <span className="mt-[2px]! block">
              {goods.salesRate > 0 && (
                <Typography variant="body13pxSemi" colorScheme="red" className="mr-[2px]">
                  {goods.salesRate}%
                </Typography>
              )}
              <Typography variant="body13pxSemi">{goods.price.toLocaleString()}원</Typography>
            </span>
          </div>
        </a>

        {/* Divider — 레거시: paddingOptions top='3' bottom='3' */}
        <div className="py-3">
          <Divider variant="line" />
        </div>

        {/* 캠페인 시작 희망일 — 레거시: Padding y="2" */}
        <div className="py-2">
          <Typography variant="body14pxMed" className="block mb-2">
            캠페인 시작 희망일
          </Typography>
          <div
            className="relative w-full px-2 border border-gray-300 rounded-[4px] flex items-center h-9 cursor-pointer"
            onClick={() => dateInputRef.current?.showPicker()}
          >
            {dateValue ? (
              <Typography>{formatDisplayDate(dateValue)}</Typography>
            ) : (
              <Typography className="text-[#ccc]">캠페인 시작 희망일을 선택해주세요.</Typography>
            )}
            <input
              type="date"
              ref={dateInputRef}
              className="absolute top-0 left-0 opacity-0 cursor-pointer h-9 w-full"
              onChange={handleDateChange}
              min={minDate}
              max={maxDate}
            />
          </div>
        </div>

        {/* 메시지 — 레거시: Padding y="2" */}
        <div className="py-2">
          <Typography variant="body14pxMed" className="block mb-2">
            브랜드 담당자에게 보낼 메세지
          </Typography>
          <TextArea
            value={messageValue}
            onChange={handleMessageChange}
            placeholder="메시지를 입력해 주세요."
            error={isMessageError}
            fullWidth
            className="h-[112px] resize-none scrollbar-hide"
            style={{ resize: 'none' }}
          />
          <div className="flex justify-between items-center mt-2">
            <Typography variant="textEtc11pxReg" colorScheme="red">
              {isMessageError ? '500자 이하로 입력해주세요.' : ''}
            </Typography>
            <Typography variant="textEtc11pxReg" colorScheme="black">
              {messageValue.length.toLocaleString()}/{MAX_LENGTH.toLocaleString()}
            </Typography>
          </div>
        </div>

        {/* 가이드 텍스트 — 레거시: GuideText body13pxReg gray-600 */}
        <div className="pt-2">
          <GuideText>
            제안 메시지 발송 이후 브랜드 수락 시 나의 협업 제안 &apos;수락&apos;버튼에 노출됩니다.
          </GuideText>
          <GuideText>브랜드가 캠페인을 제안할 시 적용 상품이 추가될 수 있습니다.</GuideText>
          <GuideText>쿠폰 정책으로 인해 할인율은 브랜드 재량에 의해 설정됩니다.</GuideText>
          <GuideText>메시지는 발송 후 수정이 불가합니다. 신중하게 작성해 주세요.</GuideText>
        </div>
      </div>

      {/* 하단 버튼 */}
      <BottomButtonWrapper className="py-3 fixed! z-20">
        <div className="flex gap-2">
          <Button variant="secondary" className="w-1/2" onClick={openCancelDialog}>
            취소
          </Button>
          <Button
            variant="primary"
            className="w-1/2"
            disabled={!dateValue || isPending}
            onClick={handleSubmit}
          >
            제안하기
          </Button>
        </div>
      </BottomButtonWrapper>
    </div>
  );
};

/** 레거시 GuideText: body13pxReg gray-600, 불릿 + pl-[11px] py-1 */
const GuideText = ({ children }: { children: React.ReactNode }) => (
  <span className="relative inline-block pl-[11px] py-1">
    <span className="absolute left-0">
      <Typography colorScheme="gray-600" variant="body13pxReg">
        •
      </Typography>
    </span>
    <Typography colorScheme="gray-600" variant="body13pxReg">
      {children}
    </Typography>
  </span>
);
